import { useRef } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { useLocalStorage } from 'usehooks-ts'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import {
  COVERAGE_SECTION_EXPAND,
  COVERAGE_SECTION_HEIGHT,
  COVERAGE_SECTION_MIN_HEIGHT,
  HEADER_OFFSET,
  RESIZE_TRACKING_GAP,
} from 'modules/shift_management/lib/consts/ShiftManagementCoverage'

interface ResizeProps {
  resizeRef: React.RefObject<HTMLDivElement>
  setHover: (hover: boolean) => void
  setHeaderHover: (hover: boolean) => void
  isResizing: { current: boolean }
  isTransitioning?: { current: boolean }
}

const Resize = ({
  resizeRef,
  setHover,
  setHeaderHover,
  isResizing,
  isTransitioning,
}: ResizeProps) => {
  const startY = useRef(0)
  const startHeight = useRef(0)
  const isCollapsedRef = useRef(false)
  const [, setLocalStorageResizeHeight] = useLocalStorage<number>(
    LOCAL_STORAGE_KEYS.shiftManagementCoverageResizeHeight,
    0
  )

  const handleMouseMove = useUnsafeCallback(
    (ev: MouseEvent) => {
      if (!isResizing.current || !resizeRef.current) return
      setHover(true)
      const deltaY = startY.current - ev.clientY
      const newHeight = startHeight.current + deltaY

      const collapsed = newHeight < COVERAGE_SECTION_HEIGHT
      isCollapsedRef.current = collapsed
      setLocalStorageResizeHeight(newHeight)
    },
    [resizeRef, setHover, isResizing, setLocalStorageResizeHeight]
  )

  const handleMouseUp = useUnsafeCallback(
    (ev: MouseEvent) => {
      isResizing.current = false
      const $coverageSection = document.querySelector('#coverage-section')
      const deltaY = startY.current - ev.clientY
      const currentHeight = startHeight.current + deltaY

      if (currentHeight > COVERAGE_SECTION_EXPAND && currentHeight < COVERAGE_SECTION_HEIGHT) {
        setLocalStorageResizeHeight(COVERAGE_SECTION_HEIGHT)
      } else if (isCollapsedRef.current) {
        setLocalStorageResizeHeight(COVERAGE_SECTION_MIN_HEIGHT)
      }

      $coverageSection?.setAttribute('data-resizing', 'false')
      track(trackingEvents.shiftManagement.resizeCoverageHeader, {
        originalSize: startHeight.current + RESIZE_TRACKING_GAP,
        targetSize: currentHeight,
      })
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    },
    [handleMouseMove, isResizing, setLocalStorageResizeHeight]
  )

  const handleMouseDown = useUnsafeCallback(
    (ev: MouseEvent) => {
      if (!resizeRef?.current || isTransitioning?.current) return

      const $coverageSection = document.querySelector('#coverage-section')

      isResizing.current = true
      startY.current = ev.clientY
      startHeight.current = resizeRef.current.offsetHeight - HEADER_OFFSET

      $coverageSection?.setAttribute('data-resizing', 'true')

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [resizeRef, isTransitioning, isResizing, handleMouseMove, handleMouseUp]
  )

  const handleMouseLeave = () => {
    setHeaderHover(true)
    if (!isResizing.current) {
      setHover(false)
    }
  }

  useUnsafeEffect(() => {
    const resizeHandle: HTMLElement | null = document.querySelector('#header-resize')
    if (resizeHandle) {
      resizeHandle.addEventListener('mousedown', handleMouseDown)
    }

    return () => {
      if (resizeHandle) {
        resizeHandle.removeEventListener('mousedown', handleMouseDown)
      }
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [handleMouseDown, handleMouseMove, handleMouseUp])

  return (
    <Box
      id="header-resize"
      onMouseEnter={() => {
        if (!isTransitioning?.current) {
          setHover(true)
          setHeaderHover(false)
        }
      }}
      onMouseLeave={handleMouseLeave}
      height="s16"
      width="full"
      position="absolute"
      style={{
        top: '-12px',
        left: '0',
        right: '0',
        zIndex: '999',
        userSelect: 'none',
        pointerEvents: isTransitioning?.current ? 'none' : 'auto',
      }}
    ></Box>
  )
}

export { Resize }
